import React, { useState } from "react";
import { Plus, ShoppingBag, Tag, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import { useAuth } from "../../context/AuthContext";
import { useServerTable } from "../../hooks/useServerTable";
import { marketplace as api, type MarketplaceListing, type ApiError } from "../../services/api";

declare global {
  interface Window {
    Razorpay: new (options: Record<string, unknown>) => { open: () => void };
  }
}
let razorpayScriptPromise: Promise<boolean> | null = null;
function loadRazorpayScript(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise(resolve => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};

const INIT = { title: "", description: "", category: "", price: "", photoBase64: "" };

const MAX_PHOTO_BASE64_LENGTH = 700_000;

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const MarketplacePage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const isModerator = user?.role === "SOCIETY_ADMIN" || user?.role === "STAFF" || user?.role === "SUPER_ADMIN";

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [buyError, setBuyError] = useState("");
  const [buyStatus, setBuyStatus] = useState<string>("");
  const [removingId, setRemovingId] = useState<string | null>(null);

  const {
    rows: items, totalElements, totalPages, loading,
    page, setPage, refetch,
  } = useServerTable<MarketplaceListing>(
    params => societyId
      ? api.listActivePage(societyId, { page: params.page, size: params.size })
      : Promise.resolve({ content: [], totalElements: 0, totalPages: 0 }),
    [societyId],
  );

  const openAdd = () => { setFormError(""); setForm(INIT); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setForm(INIT); setFormError(""); };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readFileAsBase64(file);
      if (dataUrl.length > MAX_PHOTO_BASE64_LENGTH) {
        setFormError("Photo too large (max ~500KB).");
        return;
      }
      setFormError("");
      setForm(p => ({ ...p, photoBase64: dataUrl }));
    } catch {
      setFormError("Could not read that photo.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError("");
    try {
      await api.createListing(societyId, {
        title: form.title,
        description: form.description || undefined,
        category: form.category || undefined,
        price: Number(form.price) || 0,
        photoBase64: form.photoBase64 || undefined,
      });
      refetch();
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to create listing.");
    } finally { setSaving(false); }
  };

  const handleBuy = async (listing: MarketplaceListing) => {
    if (!societyId) return;
    setBuyingId(listing.id); setBuyError(""); setBuyStatus("Creating order…");
    try {
      const order = await api.purchase(societyId, listing.id);
      setBuyStatus("Starting payment…");
      const paymentOrder = await api.createPaymentOrder(societyId, listing.id, order.id);
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setBuyError("Payment gateway failed to load.");
        setBuyingId(null);
        return;
      }
      const rzp = new window.Razorpay({
        key: paymentOrder.keyId,
        amount: Math.round(paymentOrder.amount * 100),
        currency: paymentOrder.currency,
        name: "Nexora Marketplace",
        description: listing.title,
        order_id: paymentOrder.orderId,
        handler: async (response: { razorpay_payment_id: string; razorpay_signature: string }) => {
          try {
            await api.verifyPayment(societyId, listing.id, order.id, {
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            setBuyStatus("Payment held in escrow. Seller will ship soon.");
            refetch();
          } catch (err) {
            setBuyError((err as ApiError).message || "Payment verification failed.");
          } finally { setBuyingId(null); }
        },
        modal: { ondismiss: () => setBuyingId(null) },
      });
      rzp.open();
    } catch (err) {
      setBuyError((err as ApiError).message || "Failed to start purchase.");
      setBuyingId(null);
    }
  };

  const handleRemove = async (listing: MarketplaceListing) => {
    if (!societyId) return;
    if (!window.confirm(`Remove "${listing.title}" from the marketplace?`)) return;
    setRemovingId(listing.id);
    try {
      await api.removeListing(societyId, listing.id);
      refetch();
    } catch (err) {
      setBuyError((err as ApiError).message || "Failed to remove listing.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <PageLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Community Marketplace
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {totalElements} active listing{totalElements !== 1 ? "s" : ""} — payments held in escrow until you confirm receipt
          </p>
        </div>
        <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Sell an Item
        </button>
      </div>

      {buyError && <div style={{ color: "#EF4444", fontSize: 13, marginBottom: 12 }}>{buyError}</div>}
      {buyStatus && !buyError && <div style={{ color: "var(--nx-accent)", fontSize: 13, marginBottom: 12 }}>{buyStatus}</div>}

      {loading ? (
        <div style={{ color: "var(--nx-text-muted)", fontSize: 13 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="nx-card" style={{ padding: 40, textAlign: "center", color: "var(--nx-text-muted)" }}>
          <ShoppingBag size={32} style={{ marginBottom: 8 }} />
          <p style={{ fontSize: 13 }}>No listings yet.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
          {items.map(l => {
            const isOwnListing = l.sellerResidentId === user?.id;
            const canRemove = isModerator || isOwnListing;
            return (
              <div key={l.id} className="nx-card" style={{ padding: 16 }}>
                {l.photoBase64 && (
                  <img
                    src={l.photoBase64}
                    alt={l.title}
                    style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8, marginBottom: 10 }}
                  />
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <Tag size={13} style={{ color: "var(--nx-text-muted)" }} />
                  <span style={{ fontSize: 11, color: "var(--nx-text-muted)", textTransform: "uppercase" }}>{l.category || "General"}</span>
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{l.title}</div>
                <div style={{ fontSize: 12, color: "var(--nx-text-secondary)", marginBottom: 10, minHeight: 32 }}>{l.description || "—"}</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10 }}>₹{l.price.toLocaleString("en-IN")}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {!isOwnListing && (
                    <button onClick={() => handleBuy(l)} disabled={buyingId === l.id} className="nx-btn nx-btn-primary" style={{ flex: 1 }}>
                      {buyingId === l.id ? "Processing…" : "Buy Now"}
                    </button>
                  )}
                  {canRemove && (
                    <button
                      onClick={() => handleRemove(l)}
                      disabled={removingId === l.id}
                      className="nx-btn nx-btn-outline"
                      title="Remove this listing"
                      style={{ color: "var(--nx-red, #EF4444)", flexShrink: 0, ...(isOwnListing ? { flex: 1 } : {}) }}
                    >
                      <Trash2 size={14} /> {removingId === l.id ? "Removing…" : "Remove"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 20 }}>
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 0}
            style={{
              padding: "6px 10px", borderRadius: 6, border: "1px solid var(--nx-border)",
              background: "var(--nx-surface)", cursor: page === 0 ? "not-allowed" : "pointer",
              opacity: page === 0 ? 0.4 : 1, display: "flex", alignItems: "center",
            }}
          >
            <ChevronLeft size={15} />
          </button>
          <span style={{ fontSize: 13, color: "var(--nx-text-secondary)" }}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages - 1}
            style={{
              padding: "6px 10px", borderRadius: 6, border: "1px solid var(--nx-border)",
              background: "var(--nx-surface)", cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
              opacity: page >= totalPages - 1 ? 0.4 : 1, display: "flex", alignItems: "center",
            }}
          >
            <ChevronRight size={15} />
          </button>
        </div>
      )}

      {showForm && (
        <Modal
          title="Sell an Item"
          submitLabel="List Item"
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Title *</label>
            <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required placeholder="Item name" style={inp} autoFocus />
          </div>
          <div>
            <label style={lbl}>Description</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={{ ...inp, minHeight: 60, resize: "vertical" as const }} />
          </div>
          <div>
            <label style={lbl}>Category</label>
            <input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="Furniture, Electronics..." style={inp} />
          </div>
          <div>
            <label style={lbl}>Price (₹) *</label>
            <input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} required min="0" step="0.01" style={inp} />
          </div>
          <div>
            <label style={lbl}>Photo</label>
            <input type="file" accept="image/*" onChange={handlePhotoChange} style={inp} />
            {form.photoBase64 && (
              <img
                src={form.photoBase64}
                alt="Preview"
                style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8, marginTop: 8 }}
              />
            )}
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default MarketplacePage;
